import { styled } from "styled-components";

export const Colors = {
  Brown: "#5E5E59",
  Red: "#DC5250",
  RedDark: "#A83F3D",
  Tan: "#EEE9C9",
  TanDark: "#E0DCBE",
  TanShadow: "#C7C3A8",
};

export const AltButtonVariants = {
  initial: {
    boxShadow: `0 6px 0 ${Colors.TanShadow}, 0 0px 0 ${Colors.TanShadow}`,
    color: Colors.Red,
    backgroundColor: Colors.Tan,
  },
  whileHover: {
    boxShadow: `0 3px 0 ${Colors.TanShadow}, 0 0px 0 ${Colors.TanShadow}`,
    translateY: 3,
    color: Colors.Red,
    backgroundColor: Colors.Tan,
  },
  whileTap: {
    boxShadow: `0 0px 0 ${Colors.TanShadow}, 0 -3px 0 ${Colors.TanShadow}`,
    translateY: 6,
    color: Colors.Red,
    backgroundColor: Colors.TanDark,
  },
  transition: {
    type: "spring",
    stiffness: 300,
    damping: 20,
    backgroundColor: { transition: "ease-out", duration: 0.3 },
  },
};

export const ButtonVariants = {
  initial: {
    boxShadow: `0 6px 0 ${Colors.RedDark}, 0 0px 0 ${Colors.RedDark}`,
  },
  whileHover: {
    boxShadow: `0 3px 0 ${Colors.RedDark}, 0 0px 0 ${Colors.RedDark}`,
    translateY: 3,
  },
  whileTap: {
    boxShadow: `0 0px 0 ${Colors.RedDark}, 0 -3px 0 ${Colors.RedDark}`,
    translateY: 6,
    backgroundColor: "#C94B49",
  },
  transition: {
    type: "spring",
    stiffness: 300,
    damping: 20,
    backgroundColor: { transition: "ease-out", duration: 0.3 },
  },
};

export const Header = styled.h2`
  font-size: 14px;
  line-height: 16px;
  font-weight: bold;
  text-transform: uppercase;
  color: ${Colors.Brown};
  font-family: "Space Grotesk", sans-serif;
`;

export const Paragraph = styled.p`
  font-size: 14px;
  line-height: 20px;
  color: ${Colors.Brown};
  font-family: "Space Grotesk", sans-serif;
`;

export const RawCharacter = {
  name: "Kerthoz the Desecrator",
  bio: "You are a cursed hedge wizard from the Taltherion corridor. You crave violence and endless depravity. You scaly green hide is covered in runes. You endlessly chant verses from apocalyptic prophecies.",
  stats: {
    might: 0,
    speed: 0,
    mind: 0,
    defense: 0,
  },
  bonuses: {
    might: 0,
    speed: 0,
    mind: 0,
    defense: 0,
  },
  items: [
    {
      name: "Gloves of Power",
      stat: "might",
      bonus: 3,
    },
    {
      name: "Curse of the Hedgeborn",
      stat: "mind",
      bonus: -2,
    },
    {
      name: "Beastplate",
      stat: "defense",
      bonus: 1,
    },
    {
      name: "Vile Vambraces",
      stat: "defense",
      bonus: 2,
    },
  ],
  actions: [
    {
      name: "Corpse explosion",
      stat: "might",
      description:
        "Tear apart the atomic bonds of a nearby corpse to cause a nuclear explosion. You are immune.",
    },
    {
      name: "Thought vapor",
      stat: "speed",
      description:
        "Become a mist that can enter minds and extract information.",
    },
    {
      name: "Psychomancy",
      stat: "mind",
      description:
        "Erase another being’s will to live. It destroys itself instantly.",
    },
  ],
};

export const Prompt = `You are a chatbot that speaks perfect JSON. Describe an esoteric sci-fi fantasy character existing in a brutal, galaxy-consuming war. Use the following format for describing the character.

Give the character a name.
Give the character a short description.
Give the character a blank stats object of four stats: might, speed, mind, and defense.
Describe three of the character's magic items. For each item, provide a name, an associated stat from the stat array, and a random bonus number between 1 and 6.
Describe three of the character's abilities. For each ability, provide a name, an associated stat from the stat array, and a description. If their character would damage another character, it destroys them entirely.

Here is an example of the desired output.

{
  "name": "Kerthoz the Desecrator",
  "description": "You are a cursed hedge wizard from the Taltherion corridor. You crave violence and endless depravity. You scaly green hide is covered in runes. You endlessly chant verses from apocalyptic prophecies.",
  "stats": {
    "might": 0,
    "speed": 0,
    "mind": 0,
    "defense": 0
  },
  "items": [
    {
      "name": "Gloves of Power",
      "stat": "might",
      "bonus": 3
    },
    {
      "name": "Curse of the Hedgeborn",
      "stat": "mind",
      "bonus": 5
    },
    {
      "name": "Beastplate",
      "stat": "defense",
      "bonus": 1
    },
    {
      "name": "Vile Vambraces",
      "stat": "defense",
      "bonus": 2
    }
  ],
  "actions": [
    {
      "name": "Corpse explosion",
      "stat": "might",
      "description":
        "Tear apart the atomic bonds of a nearby corpse to cause a nuclear explosion. You are immune."
    },
    {
      "name": "Thought vapor",
      "stat": "speed",
      "description":
        "Become a mist that can enter minds and extract information."
    },
    {
      "name": "Psychomancy",
      "stat": "mind",
      "description":
        "Erase another being's will to live. It destroys itself instantly."
    }
  ]
}
`;

export const EncounterPrompt = `Describe an encounter in a roleplaying game that takes place in an esoteric sci-fi fantasy campaign set in a brutal, galaxy-consuming war. Use the following format for describing the encounter. First, give the encounter a name. Then, describe the enemies in the encounter, the stakes of the encounter, and potential complications. Then, make a bullet list of key concepts and characters from the description. Finally, name three of the potential dice rolls that characters or objects in the encounter might make along with their d20 roll bonus. Write out your description in JSON. Here is an example of the desired output.
  {
    name: "The Warding Spell",
    description:
      "A group of astroliches are summoning a dreadnaught class starship just 10 lightyears from an inhabited ecumenopolis. If they complete the ritual, the world will be poisoned from orbit and converted to an army of undead in less than an hour. The liches can not be harmed during the ritual. Their spellcasting must be distracted or disrupted before they can be harmed. Nearby, lazily munching on an asteroid, is the ancient space whale Gorvanox.",
    pointsOfInterest: [
      "8 Astroliches",
      "Kaltharon, the City Planet",
      "Final Ossuary, the Lich Dreadnaught",
      "Gorvanox the Space Whale",
    ],
    rolls: [
      { name: "Astroliches", bonus: 4 },
      { name: "Final Ossuary", bonus: 20 },
      { name: "Gorvanox the Space Whale", bonus: 8 },
    ],
  };
`;

export const DeathMessages = [
  "Make a new character",
  "Die again!",
  "Kill this freak!",
  "End them!",
  "Blood for the blood god!",
  "Die one thousand deaths!",
  "Mulligan!",
  "Die again!",
  "Another one",
  "New character",
  "Get new character",
  "Another character please",
  "More grist for the mill",
  "Requisition fodder",
];
